import {
  Channel,
  KeyValuePair
} from "@directv-digital/dds.services.channel-lineup-provider";
import { ChannelFilter, SearchChoice } from "./chlupStore";
// [WAT-11766] The shared selection-identity ladder (matchId -> resourceId -> label|chnlNum). Using
// it here is what lets ONE reconciler serve the page AND the modal, whose provider-built rows carry
// no matchId. See getChoiceKey for why each rung exists.
import { getChoiceKey } from "src/components/video-lab/ChannelSearch/utils/choice-key";

/**
 * [WAT-11771] A grid/feed row plus the feed identity the parser stamps. Declared here rather
 * than read through `(channel as any)?.matchId`: `Channel` has no index signature, so a typo
 * like `matchID` is a COMPILE error instead of silently degrading every search to name-only.
 */
type ChannelRow = Channel & { matchId?: string };

/** Trim + lowercase for the name fallback; `undefined`/`null` collapse to "". */
const normalizeName = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim().toLowerCase();
};

/** Trim only — identifiers are compared case-sensitively, exactly as minted. */
const normalizeId = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
};

// [WAT-11771] Match a grid row against ONE choice the user picked from the search box.
//
// `matchId` is the identity — NOT `uuid`. Both are minted together, per channel, at the same
// two sites (server-side in channel-lineup-parser.ts, client-side in buildLocalRsnChannel for
// local/RSN rows), and they hold the same value there. They have different jobs:
//
//   • `uuid` is the DISPLAY key. The ChlupStore grid builders OVERWRITE it per bucket
//     (`Included-${uuid}`, `AddOn-${name}-${chnlNum}`, `Structural-…`) so React keys stay
//     unique when one channel appears in several buckets. It is therefore NOT comparable
//     across the choice and grid sides.
//   • `matchId` is the FEED identity. The builders spread the row and rewrite only `uuid`, so
//     `matchId` reaches the grid row untouched. That is what makes this comparison possible.
//
// 🔴 An earlier version of this fix matched on `uuid`, on the premise that "every list on this
// page is a pure filter or a spread-clone over that same array". That premise is FALSE on the
// grid side, and the result was that every national Packages-tab search returned an EMPTY grid
// ("Shudder TV-557-185" vs "Included-Shudder TV-557-185" — both non-empty, never equal, and
// the mismatch returns). Do not "simplify" this back to `uuid`; the alias-rename case below
// and searchSeam.spec.ts are the guards.
//
// Two properties make `matchId` the right key, and nothing else on the row has both:
//
// 1. It is UNIQUE per row. `chnlNum` is not: it is a tuner POSITION that distinct channels
//    legitimately share. Verified on the live stage feed (516 channels, ZIP 97201): 11 channel
//    numbers are carried by more than one channel — 557 by AMC+ HD, Shudder TV AND The Walking
//    Dead Channel; 296 by Adult Swim, Cartoon Network and Cartoon Network HD; 205 by five rows
//    including two both named "Sports Mix". Letting the number decide alone (WAT-11717's
//    ordering) therefore returned every co-numbered channel for a single selection, which is
//    the reported defect. Names are not unique either — hence WAT-11767 — so name+number still
//    cannot separate that "Sports Mix" pair, and `matchId` can.
// 2. It SURVIVES the alias rename. filterAllChannelsNationalByDma (the choice side) renames a
//    national alias to its base name ("Telemundo East" -> "Telemundo") and dedupes, while
//    filterChannelsExcludedFromDisplayedPackages (the grid side) is a pure filter and keeps the
//    alias name. The rename clones the row and rewrites `name`, not the identity. So comparing
//    matchIds matches the renamed choice back to its own grid row — which is what WAT-11717's
//    number-first ordering existed to achieve, because an exact-name comparison alone returned
//    an EMPTY grid there. That must not come back.
//
// The name fallback below is for choices carrying no `matchId`: a subscription choice (an
// EmbeddedSvods entry has neither identity nor channel number) and any hand-built searchList.
// It requires BOTH normalized sides to be non-empty, so two absent values can never count
// as agreeing, and it requires the channel number to AGREE whenever both sides carry one,
// so two same-named channels still cannot collapse into a single selection.
export function channelMatchesSearchChoice(channel: ChannelRow, ch: SearchChoice): boolean {
  const channelMatchId = normalizeId(channel?.matchId);
  const choiceMatchId = normalizeId(ch?.matchId);
  if (channelMatchId && choiceMatchId) {
    return channelMatchId === choiceMatchId;
  }

  const channelName = normalizeName(channel?.name);
  const choiceValue = normalizeName(ch?.value);
  if (!channelName || !choiceValue || channelName !== choiceValue) {
    return false;
  }
  const channelChnlNum = normalizeId(channel?.chnlNum);
  const choiceChnlNum = normalizeId(ch?.chnlNum);
  return !channelChnlNum || !choiceChnlNum || channelChnlNum === choiceChnlNum;
}

// [WAT-11779 · WAT-11766] Re-point already-selected choices at the CURRENT market's rows after a
// ZIP change — chips on the page, and (via the shared identity ladder) cards in the interactive tool.
//
// The selection (`searchChannelList` in ChnlListingPage) is component state; the choice list is
// store state, rebuilt per DMA by ADD_LOCAL_RSN_CHANNELS. Nothing used to reconcile the two, and
// for a LOCAL/RSN row that is fatal, because its `matchId` is NOT market-stable:
// buildLocalRsnChannel mints it as `${localrsnUUID}-${label}-${chnlNum}`, where `localrsnUUID` is
// the row's POSITION in the beam feed and `chnlNum` is that market's tuner position. Measured live:
// Telemundo is `6-Telemundo-47` in DMA 524 (WKTB-CD, index 6 of 12) and `4-Telemundo-39` in DMA 623
// (KXTX, index 4 of 13). A chip picked in one market therefore compared a dead identity against the
// new market's row, matchId-decides returned false, every bucket emptied, and the page rendered
// "Channel is not available in Genre Packs" for a channel the market plainly carries.
//
// Re-stamp, never drop. Two rules, and both are load-bearing:
//
// 1. **Only re-stamp on an UNAMBIGUOUS name match — but never keep a DEAD identity.** Display names
//    are not unique (WAT-11767) and a market can carry two same-named locals, so when a chip's name
//    maps to 2+ choices there is no single station to re-point at and guessing would silently swap
//    the customer's selection. In that case STRIP the identity (`matchId` + `chnlNum`) so the
//    matcher falls back to its exact-name comparison and every same-named station renders.
//    [WAT-11789] Returning the chip untouched there — the original shape of this rule — shipped a
//    live production defect: a stale identity matches nothing, so the customer saw "not available"
//    for a channel the market carries.
// 2. **A chip whose channel is genuinely GONE from the new market keeps its stale identity.**
//    Dropping it would empty `searchChannelList`, which re-renders the WHOLE lineup as though the
//    customer had never searched — the "not available in Genre Packs / Signature Packages" message
//    is reachable only while the chip is still selected and matching nothing. Leaving it is what
//    makes that message correct.
//
// Returns the SAME array reference when nothing changed, so the caller's `setState` bails out
// instead of re-rendering on every store update.
export function reconcileSearchSelections(
  selected: SearchChoice[],
  choices: SearchChoice[]
): SearchChoice[] {
  if (!Array.isArray(selected) || selected.length === 0 ||
      !Array.isArray(choices) || choices.length === 0) {
    return selected;
  }

  // [WAT-11766] Identity is `getChoiceKey`, NOT `matchId` alone. That is the ladder the search box,
  // FilterChips and DropdownList already key on (`matchId` → `resourceId` → `label|chnlNum`), and
  // keying here on `matchId` alone silently no-ops on any surface whose rows sit on a lower rung.
  // The CHLUP modal is exactly that surface: its local/RSN rows come from the video-lab provider's
  // `buildChannelObject`, which emits `chnlNum` and NO `matchId` (verified in the installed
  // package), so its chips live on the `label|chnlNum` rung — market-specific, and therefore stale
  // after a ZIP change in the same way a `matchId` is. One helper, one identity ladder, every
  // consumer of the shared seam.
  const liveKeys = new Set<string>();
  const choicesByName = new Map<string, SearchChoice[]>();
  for (const choice of choices) {
    if (!choice) {
      continue;
    }
    liveKeys.add(getChoiceKey(choice));
    const name = normalizeName(choice?.value);
    if (!name) {
      continue;
    }
    const sameName = choicesByName.get(name);
    if (sameName) {
      sameName.push(choice);
    } else {
      choicesByName.set(name, [choice]);
    }
  }

  // [WAT-11789] How many SELECTED chips carry each label. Stripping a chip's identity collapses its
  // FilterChips/choice-key identity to `chnl:<label>|` — so if two chips shared a label and both
  // were stripped, they would key IDENTICALLY and clicking one would remove the other: WAT-11767
  // verbatim. Chips whose label is not unique within the selection are therefore left stale (they
  // match nothing and the honest "not available" message shows, exactly as before this fix). That
  // needs two same-named channels selected AND a move into a market where the name is also
  // ambiguous; the reported single-chip case is unaffected.
  const selectedLabelCounts = new Map<string, number>();
  for (const chip of selected) {
    const label = normalizeName(chip?.value);
    if (label) {
      selectedLabelCounts.set(label, (selectedLabelCounts.get(label) ?? 0) + 1);
    }
  }

  let changed = false;
  const reconciled = selected.map(chip => {
    // A subscription chip is not DMA-scoped and carries no identity to refresh; it also would
    // NEVER appear in `choices` (that list is channels only), so it has to be skipped
    // explicitly or rule 1 would treat every SVOD chip as an unresolvable channel.
    if (!chip || chip?.type === "svod") {
      return chip;
    }
    // Still current → nothing to do. This is the common case: the customer changed ZIP within the
    // same DMA, or changed nothing at all.
    if (liveKeys.has(getChoiceKey(chip))) {
      return chip;
    }
    // [WAT-11766] Nothing to strip and nothing to compare — a chip with no identity at all is
    // already at the bottom of the ladder. Without this, a chip that was stripped on an EARLIER
    // pass would re-enter the ambiguous branch and be "stripped" again into a fresh object every
    // time this runs, so the same-reference bail-out below would stop holding.
    //
    // 🔴 [PR #8099 review] A reviewer asked for `resourceId` to be included here, on the grounds
    // that getChoiceKey's ladder is matchId -> resourceId -> label|chnlNum and this guard stops a
    // rung short. REFUTED, deliberately, and left as-is — the test is about the fields this
    // function can RE-STAMP, not every field the key is built from:
    //   • A resourceId identifies a Shows & Movies result. Channel choices never carry one, so
    //     there is nothing to re-stamp it from; "reaching reconciliation" could only convert the
    //     customer's show pick into a channel pick, which is worse than leaving it alone.
    //   • Including it would also break the same-reference contract. A resourceId-only chip whose
    //     name is ambiguous would enter the strip branch, come back with matchId/chnlNum cleared
    //     but its resourceId — and therefore its dead key — intact, and be rebuilt into a fresh
    //     object on every single pass.
    // Returning it unchanged IS the correct handling.
    const chipMatchId = normalizeId(chip?.matchId);
    const chipChnlNum = normalizeId(chip?.chnlNum);
    if (!chipMatchId && !chipChnlNum) {
      return chip;
    }
    const sameName = choicesByName.get(normalizeName(chip?.value)) ?? [];
    if (sameName.length === 0) {
      // Rule 2 — the channel is genuinely absent from this market. KEEP the stale identity: it
      // matches nothing, which is exactly what keeps the "not available" message reachable.
      return chip;
    }
    if (sameName.length > 1) {
      // Rule 1 — two or more channels in the new market share this chip's name, so there is no
      // single station to re-point at.
      //
      // 🔴 [WAT-11789] This branch used to `return chip` untouched, and that was WRONG in a way that
      // was live in production. Declining to GUESS a station is right; keeping a DEAD identity is
      // not — a matchId minted in the previous market matches nothing here, so the customer got
      // "Channel is not available in Genre Packs / Signature Packages" for a channel the market
      // plainly carries. Reported on PBS: DMA 623 has one PBS (KERA) so the chip is stamped with
      // its identity, DMA 524 has TWO (WGTV/8, WABE/30), the name lookup returned 2, and the chip
      // stayed stale.
      //
      // Strip the identity instead and let the matcher fall back to its exact-name comparison, so
      // BOTH same-named stations render. That is honest — the market really does carry two channels
      // called "PBS" and the chip says "PBS" — and it beats showing nothing.
      //
      // `chnlNum` MUST be cleared alongside `matchId`. channelMatchesSearchChoice's last step
      // rejects a row when both sides carry a number and they differ, so leaving 623's number on
      // the chip would reject WGTV/8 and WABE/30 just as surely as the dead matchId did.
      if ((selectedLabelCounts.get(normalizeName(chip?.value)) ?? 0) > 1) {
        // Two selected chips share this label — see selectedLabelCounts. Stripping both would give
        // them the same identity key and re-open WAT-11767, so leave them alone.
        return chip;
      }
      changed = true;
      return {
        ...chip,
        matchId: undefined,
        chnlNum: undefined
      };
    }
    const [replacement] = sameName;
    // [WAT-11766] Compare KEYS, not matchIds. This used to bail when the replacement carried no
    // `matchId` — which is every row the CHLUP modal has, so the re-point silently never happened
    // there. A replacement that itself has no identity is still worth copying: it clears the chip's
    // stale one and lets the name comparison do the work.
    if (getChoiceKey(replacement) === getChoiceKey(chip)) {
      return chip;
    }
    changed = true;
    // Identity fields ONLY. The chip's own label, type and packages are what the search box and
    // FilterChips render, and re-stamping those would change what the customer sees they picked.
    return {
      ...chip,
      matchId: replacement.matchId,
      chnlNum: replacement.chnlNum
    };
  });

  return changed ? reconciled : selected;
}

export function queryChannels(filter: ChannelFilter, channels?: Channel[]) {
  const filtered = [];
  for (const channel of channels) {
    if (filter?.product &&
      ((channel?.isStream && filter?.product === 'satellite') || (channel?.isSatellite && filter?.product === 'stream'))) {
      continue;
    }
    // [WAT-11771] Identity match against the SELECTED choice, not a substring containment
    // check — `searchList` here is the list of choices the user actually picked from the
    // dropdown (each built 1:1 from a real channel by getSearchChoices in chlupStore.ts),
    // not free-typed text still being narrowed. A `.includes()` match let selecting "ABC"
    // also pull in "ABC Localish" and "ABC News Live"; keying on the channel number alone
    // (WAT-11717) then let selecting "Shudder TV" pull in every other channel parked on
    // 557. See channelMatchesSearchChoice above for why `matchId` is the key.
    if (filter?.searchList &&
        !filter?.searchList?.some?.((ch: SearchChoice) => channelMatchesSearchChoice(channel, ch))) {
      continue;
    }
    if (filter?.categories &&
        !filter?.categories?.some((ctg: string) => ctg === channel?.category)) {
      continue;
    }
    if (filter?.chnlPackages?.length > 0 &&
        !filter?.chnlPackages?.some((pkg: string) => channel?.chnlPackages?.includes(pkg))) {
      continue;
    }
    if (filter?.addOnPromoAny &&
      channel?.isAddOnPromoAny !== filter?.addOnPromoAny) {
      continue;
    }
    if (filter?.includedAny &&
      channel?.isInclAny !== filter?.includedAny) {
      continue;
    }
    if (filter?.local &&
      ((channel?.category === "LOCAL") !== filter?.local)) {
      continue;
    }
    if (filter?.rsn &&
      (channel?.category === "RSN") !== filter?.rsn) {
      continue;
    }
    if (filter?.myFreeDtv &&
        channel?.isMyFreeDTV !== filter?.myFreeDtv) {
      continue;
    }
    filtered.push(channel);
  }

  // console.log(`STORE == filtered channel block from ${channels.length} to ${filtered.length} channels`);
  return filtered;
};

export function filterCategoricalChannels(
  filter: ChannelFilter, channels: any
) {
  const filteredChannels = {};

  // eslint-disable-next-line guard-for-in
  for (const key in channels) {
    const data = channels[key];

    if (Array.isArray(data)) {
      const filteredCtgy = queryChannels(filter, data);
      if (filteredCtgy?.length > 0) {
        filteredChannels[key] = filteredCtgy;
      }
    } else {
      const filteredSubcategory = filterCategoricalChannels(filter, data);
      if (Object.keys(filteredSubcategory).length > 0) {
        filteredChannels[key] = filteredSubcategory;
      }
    }
  }
  return filteredChannels;
};

export const orderCategoriesSubarray = (refCategories: KeyValuePair[], unsorted: KeyValuePair[]) => {
  const addOnCtg = unsorted?.filter(ctg => ctg?.key === "ADD_ON_CHANNELS") || [] as KeyValuePair[];

  // assuming that refCategories (from server) is pre-sorted properly
  const sorted = [];
  for (const ctg of refCategories) {
    if (ctg?.key === "ADD_ON_CHANNELS") {
      continue;
    }
    const isInCategory = unsorted.some(category => category.key === ctg.key);
    if (isInCategory) {
      sorted.push(ctg);
    }
  }

  // console.log("filtered categories", sorted);
  return [...sorted, ...addOnCtg];
}

export const orderCategoriesExact = (categories: KeyValuePair[], sortKeyArray: string[]) => {
  const sorted = [];
  const ctgKeys = categories?.map(ctg => ctg?.key);

  // eslint-disable-next-line no-plusplus
  for (let srtIx = 0; srtIx < sortKeyArray.length; srtIx++) {
    if (ctgKeys.includes(sortKeyArray[srtIx])) { //
      const kvMatch = categories.find(ctg => ctg?.key === sortKeyArray[srtIx]);
      sorted.push(kvMatch);
    }
  }
  return sorted.length > 0 ? sorted : categories;
};

